<script>
import { reactive, computed, onMounted, ref } from 'vue'
import redeMarketingApi from '@/services/api/redeMarketingRepository'

export default {
  setup() {
    const state = reactive({
      resumo: null,
      loading: false,
    })
    const erro = ref(null)

    async function carregar() {
      state.loading = true
      erro.value = null
      try {
        const resp = await redeMarketingApi.Resumo()
        state.resumo = resp.data
      } catch (e) {
        erro.value = e.response?.data?.message || e.message
      } finally {
        state.loading = false
      }
    }

    const totalRanks = computed(() => {
      if (!state.resumo) return 0
      return state.resumo.distribuicaoRanks.reduce((acc, r) => acc + r.quantidadeMembros, 0)
    })

    const maxCrescimento = computed(() => {
      if (!state.resumo || state.resumo.crescimentoMensal.length === 0) return 1
      return Math.max(...state.resumo.crescimentoMensal.map((c) => c.novosMembros), 1)
    })

    function porcentagem(valor, total) {
      const denom = total !== undefined ? total : state.resumo?.totaisMembros.total || 1
      return denom === 0 ? 0 : Math.round((valor / denom) * 100)
    }

    function formatarNumero(v) {
      return Number(v || 0).toLocaleString('pt-BR', { maximumFractionDigits: 2 })
    }

    function formatarCompetencia(comp) {
      const ano = Math.floor(comp / 100)
      const mes = comp % 100
      const nomes = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']
      return `${nomes[mes - 1]}/${String(ano).slice(2)}`
    }

    function alturaColuna(v) {
      return 20 + (v / maxCrescimento.value) * 120
    }

    function posClass(idx) {
      if (idx === 0) return 'pos-ouro'
      if (idx === 1) return 'pos-prata'
      if (idx === 2) return 'pos-bronze'
      return ''
    }

    onMounted(carregar)

    return {
      state,
      erro,
      totalRanks,
      carregar,
      porcentagem,
      formatarNumero,
      formatarCompetencia,
      alturaColuna,
      posClass,
    }
  },
}
</script>

<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-3">
      <div class="d-flex align-items-center gap-2">
        <router-link to="/rede-marketing" class="btn btn-sm btn-light">
          <i class="material-icons align-middle">arrow_back</i>
        </router-link>
        <h3 class="mb-0">Dashboard da Rede</h3>
      </div>
      <button class="btn btn-sm btn-outline-primary d-flex align-items-center"
              @click="carregar" :disabled="state.loading">
        <i class="material-icons me-1" :class="{ girando: state.loading }" style="font-size: 18px;">refresh</i>
        Atualizar
      </button>
    </div>

    <div v-if="state.loading && !state.resumo" class="text-center py-5">
      <div class="spinner-border text-primary"></div>
      <p class="small text-muted mt-2">Carregando métricas...</p>
    </div>

    <div v-if="erro" class="alert alert-danger">
      <i class="material-icons align-middle me-1">error</i>
      {{ erro }}
    </div>

    <template v-if="state.resumo">
      <!-- KPI Cards -->
      <div class="row g-3 mb-3">
        <div class="col-md-6 col-lg-3">
          <div class="kpi kpi-azul text-white">
            <i class="material-icons kpi-icon">groups</i>
            <div class="kpi-valor">{{ state.resumo.totaisMembros.total }}</div>
            <div class="kpi-label">Membros totais</div>
            <div class="kpi-sub">{{ state.resumo.totaisMembros.ativos }} ativos</div>
          </div>
        </div>
        <div class="col-md-6 col-lg-3">
          <div class="kpi kpi-verde text-white">
            <i class="material-icons kpi-icon">check_circle</i>
            <div class="kpi-valor">{{ state.resumo.totaisMembros.ativos }}</div>
            <div class="kpi-label">Ativos</div>
            <div class="kpi-sub">{{ state.resumo.totaisMembros.pendentes }} pendentes</div>
          </div>
        </div>
        <div class="col-md-6 col-lg-3">
          <div class="kpi kpi-laranja text-white">
            <i class="material-icons kpi-icon">stars</i>
            <div class="kpi-valor">{{ formatarNumero(state.resumo.pontosMes.totalDistribuido) }}</div>
            <div class="kpi-label">Pontos do mês</div>
            <div class="kpi-sub">{{ state.resumo.pontosMes.lancamentos }} lançamentos</div>
          </div>
        </div>
        <div class="col-md-6 col-lg-3">
          <div class="kpi kpi-ciano text-white">
            <i class="material-icons kpi-icon">emoji_events</i>
            <div class="kpi-valor">{{ formatarNumero(state.resumo.pontosMes.fastStart) }}</div>
            <div class="kpi-label">Fast Start</div>
            <div class="kpi-sub">Equipe: {{ formatarNumero(state.resumo.pontosMes.equipeVolume) }}</div>
          </div>
        </div>
      </div>

      <!-- Linha 2: Status + Ranks -->
      <div class="row g-3 mb-3">
        <div class="col-lg-6">
          <div class="card h-100">
            <div class="card-header pb-2">
              <h5 class="mb-0"><i class="material-icons align-middle me-1">pie_chart</i> Status dos membros</h5>
            </div>
            <div class="card-body">
              <div class="barra-item">
                <div class="barra-label"><span class="dot" style="background:#28a745"></span> Ativos</div>
                <div class="barra-track"><div class="barra-fill" :style="{ width: porcentagem(state.resumo.totaisMembros.ativos) + '%', background:'#28a745' }"></div></div>
                <div class="barra-valor">{{ state.resumo.totaisMembros.ativos }}</div>
              </div>
              <div class="barra-item">
                <div class="barra-label"><span class="dot" style="background:#ffc107"></span> Pendentes</div>
                <div class="barra-track"><div class="barra-fill" :style="{ width: porcentagem(state.resumo.totaisMembros.pendentes) + '%', background:'#ffc107' }"></div></div>
                <div class="barra-valor">{{ state.resumo.totaisMembros.pendentes }}</div>
              </div>
              <div class="barra-item">
                <div class="barra-label"><span class="dot" style="background:#dc3545"></span> Suspensos</div>
                <div class="barra-track"><div class="barra-fill" :style="{ width: porcentagem(state.resumo.totaisMembros.suspensos) + '%', background:'#dc3545' }"></div></div>
                <div class="barra-valor">{{ state.resumo.totaisMembros.suspensos }}</div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-lg-6">
          <div class="card h-100">
            <div class="card-header pb-2">
              <h5 class="mb-0"><i class="material-icons align-middle me-1">military_tech</i> Distribuição por Rank</h5>
            </div>
            <div class="card-body">
              <div v-for="r in state.resumo.distribuicaoRanks" :key="r.codigoRank" class="barra-item">
                <div class="barra-label"><span class="dot" :style="{ background: r.corHex || '#94a3b8' }"></span> {{ r.nomeRank }}</div>
                <div class="barra-track">
                  <div class="barra-fill" :style="{ width: porcentagem(r.quantidadeMembros, totalRanks) + '%', background: r.corHex || '#94a3b8' }"></div>
                </div>
                <div class="barra-valor">{{ r.quantidadeMembros }}</div>
              </div>
              <p v-if="totalRanks === 0" class="small text-muted text-center mt-2 mb-0">
                Nenhum rank calculado.
                <router-link to="/rede-marketing/ranks">Recalcular agora</router-link>
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Top patrocinadores -->
      <div class="card mb-3">
        <div class="card-header pb-2">
          <h5 class="mb-0"><i class="material-icons align-middle me-1">trending_up</i> Top Patrocinadores</h5>
        </div>
        <div class="card-body p-0">
          <table class="table table-hover mb-0">
            <thead>
              <tr class="small text-uppercase text-muted">
                <th style="width: 60px;">#</th>
                <th>Nome</th>
                <th>Código</th>
                <th class="text-end">Diretos</th>
                <th class="text-end"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(p, idx) in state.resumo.topPatrocinadores" :key="p.membroId">
                <td><span class="rank-pos" :class="posClass(idx)">{{ idx + 1 }}</span></td>
                <td class="fw-semibold">{{ p.nome }}</td>
                <td><code class="text-dark">{{ p.codigoIndicacao }}</code></td>
                <td class="text-end fw-bold text-primary">{{ p.totalIndicadosDiretos }}</td>
                <td class="text-end">
                  <router-link :to="`/rede-marketing/genealogia/${p.membroId}`" class="btn btn-sm btn-outline-info">
                    <i class="material-icons" style="font-size: 16px;">account_tree</i>
                  </router-link>
                </td>
              </tr>
              <tr v-if="state.resumo.topPatrocinadores.length === 0">
                <td colspan="5" class="text-center text-muted small py-4">
                  Ainda não há patrocínios registrados.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Crescimento -->
      <div class="card">
        <div class="card-header pb-2">
          <h5 class="mb-0"><i class="material-icons align-middle me-1">show_chart</i> Crescimento (últimos 6 meses)</h5>
        </div>
        <div class="card-body">
          <div v-if="state.resumo.crescimentoMensal.length > 0" class="crescimento">
            <div v-for="c in state.resumo.crescimentoMensal" :key="c.competencia" class="barra-crescimento">
              <div class="col-altura" :style="{ height: alturaColuna(c.novosMembros) + 'px' }">
                <span class="col-valor">{{ c.novosMembros }}</span>
              </div>
              <div class="col-mes">{{ formatarCompetencia(c.competencia) }}</div>
            </div>
          </div>
          <p v-else class="small text-muted text-center mb-0">Sem dados de crescimento suficientes.</p>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.girando { animation: giro 1s linear infinite; }
@keyframes giro { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

.kpi {
  border-radius: 10px;
  padding: 16px 18px;
  position: relative;
  overflow: hidden;
  height: 100%;
}
.kpi-azul {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  box-shadow: 0 4px 12px rgba(29, 78, 216, 0.3);
}
.kpi-verde {
  background: linear-gradient(135deg, #22c55e 0%, #15803d 100%);
  box-shadow: 0 4px 12px rgba(21, 128, 61, 0.3);
}
.kpi-laranja {
  background: linear-gradient(135deg, #f97316 0%, #ea580c 100%);
  box-shadow: 0 4px 12px rgba(234, 88, 12, 0.3);
}
.kpi-ciano {
  background: linear-gradient(135deg, #0891b2 0%, #0e7490 100%);
  box-shadow: 0 4px 12px rgba(8, 145, 178, 0.3);
}
.kpi-icon {
  position: absolute;
  top: 10px;
  right: 10px;
  opacity: 0.2;
  font-size: 56px;
}
.kpi-valor {
  font-size: 2rem;
  font-weight: 700;
  line-height: 1;
}
.kpi-label {
  font-size: 0.85rem;
  font-weight: 600;
  margin-top: 6px;
}
.kpi-sub {
  font-size: 0.75rem;
  opacity: 0.85;
  margin-top: 2px;
}

.barra-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0;
}
.barra-label {
  min-width: 110px;
  font-size: 0.85rem;
  color: #495057;
  display: flex;
  align-items: center;
  gap: 6px;
}
.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
}
.barra-track {
  flex: 1;
  height: 8px;
  background: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
}
.barra-fill {
  height: 100%;
  transition: width 0.4s ease;
}
.barra-valor {
  min-width: 30px;
  text-align: right;
  font-weight: 600;
  font-size: 0.85rem;
}

.rank-pos {
  display: inline-flex;
  width: 28px;
  height: 28px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #e9ecef;
  color: #495057;
  font-weight: 700;
  font-size: 0.82rem;
}
.pos-ouro { background: #fde68a; color: #854d0e; }
.pos-prata { background: #d1d5db; color: #374151; }
.pos-bronze { background: #fed7aa; color: #7c2d12; }

.crescimento {
  display: flex;
  align-items: flex-end;
  justify-content: space-around;
  height: 170px;
  padding: 10px 0;
}
.barra-crescimento {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.col-altura {
  width: 40px;
  background: linear-gradient(180deg, #6ea8fe, #0d6efd);
  border-radius: 6px 6px 0 0;
  min-height: 20px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 4px;
  transition: height 0.5s ease;
}
.col-valor {
  color: #fff;
  font-weight: 700;
  font-size: 0.78rem;
}
.col-mes {
  margin-top: 6px;
  font-size: 0.78rem;
  color: #6c757d;
  font-weight: 600;
}
</style>
